import { useTranslation } from 'react-i18next';
import React, { memo, useCallback, useEffect, useState, useMemo } from 'react';
import cx from 'clsx';

import {
  IconRcFollow,
  IconRcUnFollow,
  IconRcFollowingLight,
  IconRcFollowingDark,
} from '@/assets/follow';
import { Button, HelperTooltip } from '@/components';
import { login, request, toastError } from '@/utils';
import { useCurrentAddressInfo, useTheme } from '@/hooks';
import style from './index.module.css';

type Props = {
  params: Record<string, string | number>;
  mutateUrl: string;
  className?: string;
  isFollowing?: boolean;
  onFollowChange?: (isFollowing: boolean) => void;
  isIconType?: boolean;
  withIcon?: boolean;
};

export const BaseFollowButton = memo((props: Props) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();

  const { params, mutateUrl, className, isFollowing, onFollowChange, isIconType, withIcon } = props;

  const [_isFollowing, setFollowing] = useState(isFollowing);
  const ensuredFollow = useMemo(() => typeof _isFollowing !== 'undefined', [_isFollowing]);
  const [isLoading, setLoading] = useState(false);

  const IconRcFollowing = isDark ? IconRcFollowingDark : IconRcFollowingLight;

  const { isVerified } = useCurrentAddressInfo();

  useEffect(() => {
    setFollowing(isFollowing);
  }, [isFollowing]);

  const handleFollowClick = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();

      if (!isVerified) {
        login();
        return;
      }

      try {
        setLoading(true);
        const res = await request.post<{ is_success: boolean }>(
          `${mutateUrl}/${_isFollowing ? 'unfollow' : 'follow'}`,
          params,
        );

        if (!res?.is_success) {
          throw new Error('failed');
        }

        const next = !_isFollowing;
        setFollowing(next);
        onFollowChange?.(next);
      } catch (error) {
        toastError(error || 'failed');
      } finally {
        setLoading(false);
      }
    },
    [isVerified, _isFollowing, mutateUrl, params, onFollowChange],
  );

  const showIcon = withIcon || isIconType;

  return ensuredFollow ? (
    <Button
      type={isIconType ? 'text' : _isFollowing ? 'default' : 'primary'}
      ghost
      disabled={!ensuredFollow}
      loading={isLoading}
      className={cx(style.followBtn, className)}
      onClick={handleFollowClick}>
      {_isFollowing ? (
        <>
          <HelperTooltip disable={!isIconType} title={t('FollowButton.follow')} placement="top">
            <div className={style.follwing}>
              {showIcon ? <IconRcFollowing className={style.icon} /> : null}
              {isIconType ? null : t('FollowButton_BaseFollowButton.19t47zl')}
            </div>
          </HelperTooltip>
          <HelperTooltip disable={!isIconType} title={t('FollowButton.1trw9m2')} placement="top">
            <div className={style.unfollow}>
              {showIcon ? <IconRcUnFollow className={style.icon} /> : null}
              {isIconType ? null : t('FollowButton_BaseFollowButton.1trw9m2')}
            </div>
          </HelperTooltip>
        </>
      ) : (
        <HelperTooltip disable={!isIconType} title={t('FollowButton.follow')} placement="top">
          <div className={style.followBtnInner}>
            {showIcon ? <IconRcFollow className={cx(style.icon, style.colorPrimary)} /> : null}
            {isIconType ? null : t('FollowButton_BaseFollowButton.yw5yvl')}
          </div>
        </HelperTooltip>
      )}
    </Button>
  ) : null;
});

export const TokenFollowBtn = ({
  chainId,
  id,
  ...rest
}: Omit<Props, 'mutateUrl' | 'params'> & {
  chainId: string;
  id: string;
}) => {
  const params = useMemo(() => ({ chain_id: chainId, id }), [chainId, id]);

  return <BaseFollowButton {...rest} params={params} mutateUrl="/token" />;
};

export const CollectionFollowBtn = ({
  chainId,
  id,
  ...rest
}: Omit<Props, 'mutateUrl' | 'params'> & {
  chainId: string;
  id: string;
}) => {
  const params = useMemo(() => ({ chain_id: chainId, id }), [chainId, id]);

  return <BaseFollowButton {...rest} mutateUrl="/collection" params={params} />;
};

export const ChannelFollowBtn = ({
  id,
  ...rest
}: Omit<Props, 'mutateUrl' | 'params'> & {
  id: string;
}) => {
  return (
    <BaseFollowButton {...rest} mutateUrl="/user/channel" params={{ channel_id: id }} withIcon />
  );
};
